import { useReducer } from 'react';
import { Subject } from 'rxjs';
import { OmnichannelChatSDK } from '@microsoft/omnichannel-chat-sdk';
import { Environment, getEnvironment, isProdEnv, type ILogger, type LiveAgentQueue } from '@bingads-webui-react/msa-copilot-common';
import ChatSDKConfig from '@microsoft/omnichannel-chat-sdk/lib/core/ChatSDKConfig';
import OmnichannelConfig from '@microsoft/omnichannel-chat-sdk/lib/core/OmnichannelConfig';

import { LiveAgentConnectionState } from '../helpers/live-agent-helper';
import { getOmnichannelConfig } from '../helpers/live-agent-config';

export type GetSessionTokenForQueueFunction = (queue?: LiveAgentQueue) => () => Promise<string | null>;

type LiveAgentChatSDKState = {
  chatSDK?: OmnichannelChatSDK,
  liveAgentState: LiveAgentConnectionState,
  liveAgentStream?: Subject<any>,
  omnichannelConfig?: OmnichannelConfig,
};

type LiveAgentChatSDKAction =
  | { type: 'initialize', createLiveAgentSessionTokenGetter: GetSessionTokenForQueueFunction, queue?: LiveAgentQueue }
  | { type: 'disconnect', logger?: ILogger, isLiveAgentOnlyMode?: boolean }
  | { type: 'in-progress' }
  | { type: 'connected' };

function createChatSDK(omnichannelConfig: OmnichannelConfig, createLiveAgentSessionTokenGetter?: GetSessionTokenForQueueFunction, queue?: LiveAgentQueue) {
  const chatSDKConfig: ChatSDKConfig = {
    chatReconnect: {
      disable: false,
    },
  };

  if (createLiveAgentSessionTokenGetter) {
    chatSDKConfig.getAuthToken = createLiveAgentSessionTokenGetter(queue);
  }

  return new OmnichannelChatSDK(omnichannelConfig, chatSDKConfig);
}

function liveAgentChatSDKReducer(state: LiveAgentChatSDKState, action: LiveAgentChatSDKAction): LiveAgentChatSDKState {
  switch (action.type) {
    case 'initialize': {
      if (state.chatSDK) {
        // already initialized, nothing to do
        return state;
      }

      const liveAgentStream = new Subject();
      const chatSDK = createChatSDK(state.omnichannelConfig!, action.createLiveAgentSessionTokenGetter, action.queue);

      return {
        ...state,
        chatSDK,
        liveAgentState: LiveAgentConnectionState.DISCONNECTED,
        liveAgentStream,
      };
    }
    case 'disconnect': {
      // cleanup old state
      state.liveAgentStream?.complete();
      state.chatSDK?.endChat().catch((ex) => {
        action.logger?.error(`Copilot - Live Agent: Error while ending chat: ${ex}`);
      });

      return {
        ...state,
        chatSDK: undefined,
        liveAgentState: LiveAgentConnectionState.DISCONNECTED,
        liveAgentStream: action?.isLiveAgentOnlyMode ? state.liveAgentStream : undefined,
      };
    }
    case 'in-progress':
      return {
        ...state,
        liveAgentState: LiveAgentConnectionState.IN_PROGRESS,
      };
    case 'connected':
      return {
        ...state,
        liveAgentState: LiveAgentConnectionState.CONNECTED,
      };
    default:
      return state;
  }
}

// Exact hosts for the two blue-green Azure Storage static-website endpoints backing
// tip.ads.microsoft.com. Direct-blob URLs (used for deploy verification / bug bash) are not
// classified as TIP by getEnvironment, so the `?liveagentenv=prod` guardrail below treats them
// like TIP — mirroring the bot-directline STATIC_SITE_HOSTS override in ads-copilot-loader.jsx.
const STATIC_SITE_HOSTS = [
  'adsuxtipstaticwebsite.z5.web.core.windows.net',
  'adsuxtipstaticwebsite2.z5.web.core.windows.net',
];

// `?liveagentenv=prod` forces Prod Omnichannel widget config. Only honored on TIP (or the
// TIP-backing static-site hosts) as a guardrail.
const isLiveAgentProdUrlOverride = () => {
  try {
    const isTipOrStaticSite = getEnvironment(window.location.host, window.location.search) === Environment.TIP
      || STATIC_SITE_HOSTS.includes(window.location.hostname);
    if (!isTipOrStaticSite) {
      return false;
    }
    return new URLSearchParams(window.location.search.toLowerCase()).get('liveagentenv') === 'prod';
  } catch {
    return false;
  }
};

export function useLiveAgentChatSDKReducer(
  locale: string,
  isAuthenticated: boolean,
  createLiveAgentSessionTokenGetter?: (queue?: LiveAgentQueue) => () => Promise<string | null>,
  isStartLiveAgentStreamingImmediately?: boolean,
  logger?: ILogger
) {
  return useReducer(
    liveAgentChatSDKReducer,
    { liveAgentState: LiveAgentConnectionState.DISCONNECTED },
    (initialState) => {
      const isProd = isProdEnv() || isLiveAgentProdUrlOverride();
      const omnichannelConfig = getOmnichannelConfig(locale, isProd, isAuthenticated && !!createLiveAgentSessionTokenGetter);

      if (isStartLiveAgentStreamingImmediately) {
        logger?.trace('Copilot - Live Agent: immediately start the chat');
        const liveAgentStream = new Subject();
        const chatSDK = createChatSDK(omnichannelConfig, createLiveAgentSessionTokenGetter);
        return {
          ...initialState,
          chatSDK,
          liveAgentStream,
        };
      }

      return {
        ...initialState,
        omnichannelConfig,
      };
    }
  );
}
